import React, { Component } from "react";
import { getEmailLog } from "../../../services/admin";
import { RETRYABLE_STATUSES, StatusBadge, formatInTimezone, shortClassName } from "./helpers";

function Field(props) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: "#777", textTransform: "uppercase" }}>{props.label}</div>
      <div style={{ fontSize: 13, wordBreak: "break-word" }}>{props.value || "-"}</div>
    </div>
  );
}

// Full-screen overlay showing one email log: delivery details plus a preview of the
// stored body. The HTML is rendered in a sandboxed iframe (no scripts, no same-origin)
// so stored email markup can never run inside the admin app.
class EmailLogDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      log: null,
      loading: true,
      view: "html",
    };
  }

  componentDidMount() {
    this.loadLog();
  }

  componentDidUpdate(prevProps) {
    // Parent bumps refreshKey after a retry/sync so the overlay shows the new status.
    if (prevProps.logId !== this.props.logId || prevProps.refreshKey !== this.props.refreshKey) {
      this.loadLog();
    }
  }

  loadLog() {
    this.setState({ loading: true });

    getEmailLog(this.props.logId)
      .then(function(res) {
        if (res && res.data && res.data.status) {
          var log = res.data.data;
          this.setState({ log: log, view: log.body ? "html" : "text" });
        } else {
          this.setState({ log: null });
        }
      }.bind(this))
      .catch(function() { this.setState({ log: null }); }.bind(this))
      .finally(function() { this.setState({ loading: false }); }.bind(this));
  }

  renderBody(log) {
    if (this.state.view === "html") {
      if (!log.body) {
        return <div style={{ padding: 20, color: "#777" }}>No HTML body stored.</div>;
      }
      return (
        <iframe
          title={"email-log-" + log.id}
          sandbox=""
          srcDoc={log.body}
          style={{ width: "100%", height: 480, border: "1px solid #e0e0e0", borderRadius: 4, background: "#fff" }}
        />
      );
    }

    return (
      <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, maxHeight: 480, overflow: "auto", background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 4, padding: 12 }}>
        {log.text_body || "No plain text body stored."}
      </pre>
    );
  }

  render() {
    var log = this.state.log;
    var loading = this.state.loading;
    var tz = this.props.adminTimezone;
    var isRetrying = this.props.isRetrying;
    var isSyncing = this.props.isSyncing;

    var user = log && log.user ? ((log.user.first_name || "") + " " + (log.user.last_name || "")).trim() + " (#" + log.user_id + ")" : (log && log.user_id ? "#" + log.user_id : "");
    var delivery = log && log.delivery_code ? log.delivery_code + " " + (log.delivery_message || "") : (log ? log.delivery_message : "");

    return (
      <div
        onClick={this.props.onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}
      >
        <div
          onClick={function(e) { e.stopPropagation(); }}
          role="dialog"
          aria-modal="true"
          style={{ background: "#fff", borderRadius: 8, width: "100%", maxWidth: 960, padding: 20 }}
        >
          <div className="d-flex" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
            <h5 style={{ margin: 0, wordBreak: "break-word" }}>{log ? log.subject || "(no subject)" : "Email log"}</h5>
            <button className="btn btn-sm btn-secondary" onClick={this.props.onClose}>Close</button>
          </div>

          {loading && !log && (
            <div className="text-center py-4">
              <div className="custom-loader">
                <div className="loader-dot"></div>
                <div className="loader-dot"></div>
                <div className="loader-dot"></div>
              </div>
            </div>
          )}

          {!loading && !log && <div style={{ padding: 20 }}>Email log not found.</div>}

          {log && (
            <div>
              <div className="d-flex" style={{ gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
                <StatusBadge status={log.status} />
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  {log.message_id && (
                    <button className="btn btn-sm btn-primary" disabled={isSyncing} onClick={this.props.onSync.bind(null, log.id)}>
                      {isSyncing ? "Syncing..." : "Sync with Mailgun"}
                    </button>
                  )}
                  {RETRYABLE_STATUSES.indexOf(log.status) !== -1 && (
                    <button className="btn btn-sm btn-warning" disabled={isRetrying} onClick={this.props.onRetry.bind(null, log.id)}>
                      {isRetrying ? "Queuing..." : "Retry"}
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "4px 16px", marginBottom: 16 }}>
                <Field label="To" value={log.recipient_name ? log.recipient_name + " <" + log.recipient_email + ">" : log.recipient_email} />
                <Field label="From" value={log.sender_email} />
                <Field label="CC" value={(log.cc || []).join(", ")} />
                <Field label="BCC" value={(log.bcc || []).join(", ")} />
                <Field label="Type" value={shortClassName(log.mailable_class)} />
                <Field label="User" value={user} />
                <Field label="Appointment ID" value={log.appointment_id} />
                <Field label="Message ID" value={log.message_id} />
                <Field label="Created" value={formatInTimezone(log.created_at, tz)} />
                <Field label="Sent" value={formatInTimezone(log.sent_at, tz)} />
                <Field label="Delivered" value={formatInTimezone(log.delivered_at, tz)} />
                <Field label="Failed" value={formatInTimezone(log.failed_at, tz)} />
                <Field label="Retries" value={log.retry_count ? log.retry_count + " (last " + formatInTimezone(log.last_retried_at, tz) + ")" : "0"} />
                <Field label="Delivery response" value={delivery} />
                <Field label="Delivery description" value={log.delivery_description} />
                <Field label="Error" value={log.error_message} />
              </div>

              <div className="d-flex" style={{ gap: 8, marginBottom: 8 }}>
                <button className={"btn btn-sm " + (this.state.view === "html" ? "btn-dark" : "btn-outline-dark")} onClick={function() { this.setState({ view: "html" }); }.bind(this)}>
                  HTML preview
                </button>
                <button className={"btn btn-sm " + (this.state.view === "text" ? "btn-dark" : "btn-outline-dark")} onClick={function() { this.setState({ view: "text" }); }.bind(this)}>
                  Plain text
                </button>
              </div>

              {this.renderBody(log)}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default EmailLogDetail;
